import { useState } from 'react';
import { cn } from '@/lib/utils';
import { LabelingProvider } from '../context/LabelingProvider';
import { useLabeling } from '../context/useLabeling';
import { LabelingToolbar } from '../components/LabelingToolbar';
import { FocusMode } from '../components/focus/FocusMode';
import { GridMode } from '../components/grid/GridMode';
import { DichotomousMode } from '../components/dichotomous/DichotomousMode';
import { ToolSelector } from '../components/ToolSelector';

type Step = 'selector' | 'labeling';

/** Who scrolls: the page as a whole, or the panels inside a screen pinned to the viewport. */
type Scroll = 'page' | 'panels';

/**
 * The area the shell gives the page, in one of two shapes.
 *
 * `panels` pins the screen to the height of that area so that its own panels (experiment list,
 * image series, label panel) scroll and the image viewer stays in view. `h-full` alone cannot do
 * it: the shell's content wrapper is a flex item whose minimum height is its content, so a long
 * list simply made the whole page thousands of pixels tall (since 1.16.8, when the footer moved
 * inside the scrolling `<main>`). The absolutely positioned layer has no in-flow content, hence
 * nothing for the wrapper to grow to.
 *
 * `page` lets the screen take the height it needs and the shell's `<main>` scroll. That is what
 * the grid of cards wants: pinned, the grid shared its bounded height between its rows (the cards
 * clip their overflow, which zeroes their minimum size) and 200 images became 200 thin lines.
 *
 * Both shapes render the same two wrappers so that switching modes keeps the children mounted.
 */
function LabelingArea({ scroll, children }: { scroll: Scroll; children: React.ReactNode }) {
  const pinned = scroll === 'panels';
  return (
    <div
      className={pinned ? 'relative min-h-0 flex-1' : 'flex flex-col'}
      data-testid="labeling-area"
      data-scroll={scroll}
    >
      <div className={cn('flex flex-col', pinned && 'absolute inset-0')}>{children}</div>
    </div>
  );
}

export function LabelingPage() {
  const [step, setStep] = useState<Step>('selector');
  const [activeToolId, setActiveToolId] = useState<string | null>(null);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const handleStart = (toolId: string, sessionId: string) => {
    setActiveToolId(toolId);
    setActiveSessionId(sessionId);
    setStep('labeling');
  };

  const handleChangeTool = () => {
    setStep('selector');
  };

  if (step === 'labeling' && activeToolId && activeSessionId) {
    return (
      <LabelingProvider toolId={activeToolId} sessionId={activeSessionId}>
        <LabelingPageInner onChangeTool={handleChangeTool} />
      </LabelingProvider>
    );
  }

  return (
    <LabelingArea scroll="panels">
      <ToolSelector onStart={handleStart} />
    </LabelingArea>
  );
}

interface LabelingPageInnerProps {
  onChangeTool: () => void;
}

function LabelingPageInner({ onChangeTool }: LabelingPageInnerProps) {
  const { mode } = useLabeling();
  const scroll: Scroll = mode === 'grid' ? 'page' : 'panels';

  return (
    <LabelingArea scroll={scroll}>
      <div
        className={cn('flex flex-col gap-4 p-4', scroll === 'panels' && 'h-full overflow-hidden')}
      >
        <LabelingToolbar onChangeTool={onChangeTool} />
        {mode === 'focus' ? <FocusMode /> : mode === 'grid' ? <GridMode /> : <DichotomousMode />}
      </div>
    </LabelingArea>
  );
}
